import type { RefreshPricingOptions } from './useCartPricing'
import { usePromotionModule } from './usePromotionModule'

import {
  ApiValidationError,
  parseApiError,
} from '#Common/models/Error'

function resolvePromoCodeError(error: unknown) {
  const apiError = parseApiError(error)

  return (
    ApiValidationError.getFieldMessage(apiError, 'code')
    ?? ApiValidationError.getMessages(apiError)?.[0]
    ?? apiError?.title
  )
}

export const usePromoCode = (
  refreshPricing: (options?: RefreshPricingOptions) => Promise<void>,
  hasSelectedItems: Ref<boolean>,
) => {
  const { service, store } = usePromotionModule()

  const inputCode = ref('')
  const promoCodeError = ref<string | null>(null)

  watch(
    () => store.activePromoCode,
    (code) => {
      inputCode.value = code ?? ''
    },
    { immediate: true },
  )

  watch(inputCode, () => {
    promoCodeError.value = null
  })

  async function applyPromoCode() {
    const code = inputCode.value.trim()
    if (!code) return

    promoCodeError.value = null

    try {
      await service.setPromoCode(code)
      await refreshPricing(
        hasSelectedItems.value ? undefined : { activePromoCode: code },
      )
    } catch (error) {
      promoCodeError.value = resolvePromoCodeError(error) ?? null
    }
  }

  async function clearPromoCode() {
    promoCodeError.value = null
    await service.clearPromoCode()
    inputCode.value = ''
    await refreshPricing(
      hasSelectedItems.value ? undefined : { activePromoCode: null },
    )
  }

  const hasSavedPromoWithoutSelection = computed(() =>
    Boolean(store.activePromoCode) && !hasSelectedItems.value,
  )

  return {
    inputCode,
    promoCodeError,
    activePromoCode: computed(() => store.activePromoCode),
    applyPromoCode,
    clearPromoCode,
    hasSavedPromoWithoutSelection,
  }
}
